import { Button, Stack } from '@chakra-ui/react';
import { getAsset } from '@pelicargo/tenants';
import { useError } from '@pelicargo/ui';
import { accountTypeOptions, freightServiceOptions, slugify, surchargeCategoryOptions } from '@pelicargo/utils';
import { useFormContext, useWatch } from 'react-hook-form';

import { trpc } from '../../config/trpc';
import { AirportInput } from '../Autocomplete/AirportInput';
import { SimpleImageUpload } from '../SimpleImageUpload';
import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleSwitch } from '../SimpleSwitch';

export const ProviderForm = () => {
  const { handleError } = useError();
  const { setValue, setError } = useFormContext();
  const name = useWatch({ name: 'name' });

  const { data: allAccounts } = trpc.getAllAccounts.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const { mutateAsync: getUploadUrl } = trpc.getUploadUrl.useMutation();

  const uploadFile = async (file: File) => {
    try {
      if (!file) return;

      const { uploadUrl, getUrl } = await getUploadUrl({
        filename: file.name,
        bucket: 'airlineLogos',
      });

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': 'image/*' }),
        body: file,
      });

      if (response.status != 200) throw new Error(response.statusText);

      setValue('logo_uri', getUrl);
    } catch (error) {
      handleError(error);
    }
  };

  const handleGenerateId = () => {
    if (!name?.trim()) {
      setError('id', { message: 'Name is required to generate identifier' });
      return;
    }

    const generatedId = slugify(name);

    setValue('id', generatedId);
  };

  return (
    <>
      <Stack direction="column" spacing="4" w="full">
        <SimpleImageUpload
          name="logo_uri"
          defaultSrc={getAsset('airlines-logo/airline_placeholder.svg')}
          onUpload={uploadFile}
        />
        <SimpleInput name="name" label="Provider Name" placeholder="Air Canada" isRequired />
        <Stack direction="row" align="start">
          <Stack flex="1">
            <SimpleInput name="id" label="Identifier (IATA, SCAC, etc.)" placeholder="AC" isRequired />
            <Button
              onClick={handleGenerateId}
              size="xs"
              isDisabled={!name?.trim()}
              title={!name?.trim() ? 'Enter a name to generate' : 'Generate identifier from name'}
            >
              Generate from name
            </Button>
          </Stack>
          <Stack w="50%">
            <SimpleInput name="awb_prefix" label="AWB" placeholder="014" flex="1" />
            <SimpleInput
              name="direct_tracking_url"
              label="Direct tracking URL"
              placeholder="https://example.com/en/cargo/track-shipment/%PREFIX%-%SUFFIX%"
              flex="1"
            />
          </Stack>
        </Stack>
        <SimpleInput
          name="terms_conditions_url"
          label="Airline Terms & Conditions"
          placeholder="https://example.com/terms"
          isRequired
        />
        <AirportInput name="airports" label="Airports" isMulti />
        <SimpleReactSelect name="supported_account_types" label="Account Types" options={accountTypeOptions} isMulti />
        <SimpleReactSelect
          name="required_surcharge_categories"
          label="Required Surcharge Categories"
          options={surchargeCategoryOptions}
          isMulti
        />
        <SimpleReactSelect name="provided_services" label="Provided Services" options={freightServiceOptions} isMulti />
        <SimpleReactSelect
          name="provider_accounts"
          label="Blacklisted Accounts"
          options={allAccounts?.map((account) => ({
            label: account?.name,
            value: String(account?.id),
          }))}
          isSearchable
          isMulti
        />
        <SimpleSwitch name="is_active" label="Active" />
        <SimpleSwitch name="allows_pelicargo_settled_payments" label="Allows Pelicargo Settled Payments" />
      </Stack>
    </>
  );
};
